"use client";

import { Close } from "@mui/icons-material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import React, { useEffect, useRef, useState } from "react";

import { useMatchBreakpoints } from "../../hooks";
import { Button, ButtonSize, ButtonVariant } from "../Button";
import { Text, TextColor, TextSize, TextWeight } from "../Text";

export interface FilterOption {
  id: string;
  label: string;
  count: number;
  color?: string;
}

type FilteringProps = {
  filterLabel: string;
  filterOptions: FilterOption[];
  selectedOptions: FilterOption[];
  setSelectedOptions: (
    options: FilterOption[] | ((prev: FilterOption[]) => FilterOption[]),
  ) => void;
  onApplyFilters: (
    colors: FilterOption[],
    sizes: FilterOption[],
    features: FilterOption[],
    minPrice: number | null,
    maxPrice: number | null,
    onSaleOnly: boolean,
  ) => void;
  allSelectedColors: FilterOption[];
  allSelectedSizes: FilterOption[];
  currentMinPrice: number | null;
  currentMaxPrice: number | null;
  currentOnSaleOnly: boolean;
  currentFeatures: FilterOption[];
  filterType?: "color" | "size" | "feature" | string;
};

const Filtering: React.FC<FilteringProps> = ({
  filterLabel,
  filterOptions,
  selectedOptions,
  setSelectedOptions,
  onApplyFilters,
  allSelectedColors,
  allSelectedSizes,
  currentMinPrice,
  currentMaxPrice,
  currentOnSaleOnly,
  currentFeatures,
  filterType,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [tempSelectedOptions, setTempSelectedOptions] = useState<
    FilterOption[]
  >([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isXs, isSm, isMd } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  // Synchronizuj tymczasowe opcje z wybranymi opcjami gdy dropdown się otwiera
  useEffect(() => {
    if (isOpen) {
      setTempSelectedOptions([...selectedOptions]);
    }
  }, [isOpen, selectedOptions]);

  // Hook do obsługi kliknięć poza dropdown z automatycznym zastosowaniem filtrów
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isOpen &&
        !isMobile // Tylko dla desktop dropdown
      ) {
        // Automatycznie zastosuj filtry i zamknij dropdown
        handleApply();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobile, tempSelectedOptions]);

  const handleOptionChange = (optionId: string) => {
    setTempSelectedOptions((prev) =>
      prev.some((o) => o.id === optionId)
        ? prev.filter((o) => o.id !== optionId)
        : [...prev, filterOptions.find((o) => o.id === optionId)!],
    );
  };

  const handleApply = () => {
    // Zaktualizuj wybrane opcje
    setSelectedOptions(tempSelectedOptions);

    // Zastosuj filtry na podstawie typu filtra
    let colors = allSelectedColors;
    let sizes = allSelectedSizes;
    let features = currentFeatures;

    // Wykryj typ filtra automatycznie jeśli nie podano
    const detectedFilterType =
      filterType ||
      (filterLabel.toLowerCase() === "kolor"
        ? "color"
        : filterLabel.toLowerCase() === "rozmiar"
          ? "size"
          : filterLabel.toLowerCase() === "cecha"
            ? "feature"
            : null);

    if (detectedFilterType === "color") {
      colors = tempSelectedOptions;
    } else if (detectedFilterType === "size") {
      sizes = tempSelectedOptions;
    } else if (detectedFilterType === "feature") {
      // Stara logika dla ogólnego typu "feature"
      features = tempSelectedOptions;
    } else if (detectedFilterType?.startsWith("feature-")) {
      // Nowa logika dla specyficznych grup cech
      // Nie wywołujmy onApplyFilters - SortingFiltering sam to zrobi po aktualizacji stanu
      setIsOpen(false);
      return;
    }

    onApplyFilters(
      colors,
      sizes,
      features,
      currentMinPrice,
      currentMaxPrice,
      currentOnSaleOnly,
    );
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempSelectedOptions([]);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const renderContent = () => {
    return (
      <>
        <div className="max-h-[320px] overflow-y-auto">
          {filterOptions.map((option) => (
            <label
              key={option.id}
              className={`flex items-center justify-between cursor-pointer group px-3 py-3 transition-colors duration-200 ${
                tempSelectedOptions.some((o) => o.id === option.id)
                  ? "bg-gray-100"
                  : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center">
                <div className="relative">
                  <input
                    type="checkbox"
                    value={option.id}
                    checked={tempSelectedOptions.some(
                      (o) => o.id === option.id,
                    )}
                    onChange={() => handleOptionChange(option.id)}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 border-2 flex items-center justify-center ${
                      tempSelectedOptions.some((o) => o.id === option.id)
                        ? "border-black bg-black"
                        : "border-gray-300 bg-white group-hover:border-gray-400"
                    }`}
                  >
                    {tempSelectedOptions.some((o) => o.id === option.id) && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Color indicator - only shown if color exists */}
                {option.color && (
                  <div
                    className="w-4 h-4 ml-4 border border-gray-300"
                    style={{ backgroundColor: option.color }}
                  />
                )}

                <span className={`${option.color ? "ml-4" : "ml-3"} text-sm`}>
                  <Text
                    text={option.label}
                    fontSize={TextSize.S}
                    weight={TextWeight.SEMIBOLD}
                    color={TextColor.GRAYSCALE_600}
                  />
                </span>
              </div>

              <Text
                text={`${option.count}`}
                fontSize={TextSize.XS}
                color={TextColor.GRAYSCALE_500}
              />
            </label>
          ))}
        </div>

        <div className="flex gap-2 px-3 pt-4 pb-2 w-full">
          <Button
            text="Wyczyść"
            fullWidth
            className="!w-full"
            size={ButtonSize.S}
            onClick={handleClear}
            variant={ButtonVariant.SECONDARY}
          />
          <Button
            text="Zastosuj"
            fullWidth
            className="!w-full"
            size={ButtonSize.S}
            onClick={handleApply}
          />
        </div>
      </>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-fit flex items-center justify-between gap-2 cursor-pointer transition-colors duration-200"
        onClick={toggleDropdown}
      >
        <Text
          text={filterLabel}
          fontSize={TextSize.XS}
          textTransform="uppercase"
          className="tracking-widest"
          weight={
            selectedOptions && selectedOptions.length > 0
              ? TextWeight.BOLD
              : TextWeight.NORMAL
          }
        />
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Filter Options - collapsible */}
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={isOpen}
          onClose={() => handleApply()}
          onOpen={() => setIsOpen(true)}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <div className="p-4">
            <div
              className="flex justify-end mb-2 cursor-pointer"
              onClick={() => setIsOpen(false)}
            >
              <Close />
            </div>

            {renderContent()}
          </div>
        </SwipeableDrawer>
      ) : (
        /* Desktop Dropdown */
        isOpen && (
          <div className="absolute top-8 left-0 border border-gray-200 bg-white min-w-[320px] py-2 shadow-lg z-10">
            {renderContent()}
          </div>
        )
      )}
    </div>
  );
};

export default Filtering;
